import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  Button,
  DropdownMenu,
  NewBadge,
  Tooltip,
  ChevronDownIcon,
  PlusIcon,
  Flex,
  Text,
} from '@buffer-mono/popcorn'

import { SearchImagesModal } from './components/search-images-modal/SearchImagesModal'
import type {
  IntegrationsContext,
  IntegrationsService,
  IntegrationsServiceSubItem,
} from './types'

import styles from './IntegrationsBar.module.css'

/**
 * Renders a sub-item's label. When the sub-item carries a `description`,
 * it shows the bold title with the muted description beside it on the same
 * row; otherwise it renders the label inline.
 */
function SubItemLabel({
  subItem,
}: {
  subItem: IntegrationsServiceSubItem
}): JSX.Element {
  if (!subItem.description) {
    return <>{subItem.label}</>
  }

  return (
    <Flex
      direction="row"
      align="center"
      gap="2xs"
      className={styles.ratioOption}
    >
      {/* medium weight matches the parent integration items' label style */}
      <Text size="md" weight="medium">
        {subItem.label}
      </Text>
      <Text size="sm" color="subtle">
        {subItem.description}
      </Text>
    </Flex>
  )
}

/**
 *  COMPONENT: IDEAS INTEGRATIONS BAR
 */
export const IntegrationsBar = ({
  context,
  services,
  activeService,
  disableBar = false,
  container,
  children,
}: {
  context: IntegrationsContext
  services: Array<IntegrationsService>
  activeService: IntegrationsService | null
  disableBar?: boolean
  container?: HTMLElement | null
  children?: React.ReactNode
}): JSX.Element | null => {
  const [lastIntegrationUsed, setLastIntegrationUsed] = useState(
    localStorage.getItem('last_used_integration'),
  )

  const [sawNewIndicator, setSawNewIndicator] = useState(
    !!localStorage.getItem('integration_bar_seen_new_indicator'),
  )

  if (services.length === 0 && !children) return null

  const storedIntegration = services.find(
    (e) => e.title === lastIntegrationUsed,
  )

  const hasNewTags = !!services.find((e) => e.isNew) && !sawNewIndicator

  const pinnedTooltipLabel =
    disableBar || !storedIntegration
      ? 'No more media can be attached'
      : storedIntegration.label

  const tooltipLabel = disableBar
    ? 'No more media can be attached'
    : 'Select media from integrations'

  const onServiceClick = (service: IntegrationsService): void => {
    localStorage.setItem('last_used_integration', service.title)
    setLastIntegrationUsed(service.title)
    service.onClick?.()
  }

  const onServiceSubItemClick = (
    service: IntegrationsService,
    subItem: IntegrationsServiceSubItem,
  ): void => {
    localStorage.setItem('last_used_integration', service.title)
    setLastIntegrationUsed(service.title)
    subItem.onClick()
  }

  const removeNewIndicator = (): void => {
    localStorage.setItem('integration_bar_seen_new_indicator', 'true')
    setSawNewIndicator(true)
  }

  return (
    <>
      <div
        className={styles.integrationsBarContainer}
        data-testid="integrations-bar"
      >
        <Button.Group variant="tertiary">
          {storedIntegration &&
            (storedIntegration.subItems?.length ? (
              <DropdownMenu>
                <Tooltip content={pinnedTooltipLabel}>
                  <DropdownMenu.Trigger disabled={disableBar}>
                    <Button
                      disabled={disableBar}
                      aria-label={storedIntegration.label}
                      className={styles.mainButton}
                    >
                      {storedIntegration.icon}
                    </Button>
                  </DropdownMenu.Trigger>
                </Tooltip>
                <DropdownMenu.Content
                  align="start"
                  className={styles.ratioMenu}
                >
                  {storedIntegration.subItems.map((subItem) => (
                    <DropdownMenu.Item
                      key={subItem.id}
                      data-testid={`integrations-bar-${storedIntegration.id}-${subItem.id}`}
                      disabled={disableBar}
                      onClick={(): void =>
                        onServiceSubItemClick(storedIntegration, subItem)
                      }
                    >
                      <SubItemLabel subItem={subItem} />
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu>
            ) : (
              <Tooltip content={pinnedTooltipLabel}>
                <Button
                  onClick={(): void => storedIntegration.onClick?.()}
                  disabled={disableBar}
                  aria-label={storedIntegration.label}
                  className={styles.mainButton}
                >
                  {storedIntegration.icon}
                </Button>
              </Tooltip>
            ))}

          <DropdownMenu onOpenChange={removeNewIndicator}>
            <Tooltip content={tooltipLabel}>
              <DropdownMenu.Trigger disabled={disableBar}>
                <Button
                  aria-label="More integration options"
                  id="grouped-integrations-button"
                  data-testid="grouped-integrations-button"
                  disabled={disableBar}
                  className={clsx(
                    styles.dropdownTrigger,
                    hasNewTags && styles.hasNewTags,
                    !storedIntegration && styles.addMediaIconShown,
                  )}
                >
                  {!storedIntegration && (
                    <PlusIcon className={styles.addMediaIcon} />
                  )}
                  <ChevronDownIcon />
                </Button>
              </DropdownMenu.Trigger>
            </Tooltip>

            <DropdownMenu.Content align="start">
              {services.map((service) =>
                service.subItems?.length ? (
                  <DropdownMenu.Sub key={service.id}>
                    <DropdownMenu.SubTrigger
                      data-testid={`integrations-bar-${service.id}`}
                      disabled={disableBar}
                    >
                      <Flex align="center" gap="2xs">
                        {service.icon} {service.title}
                      </Flex>
                      {service.isNew && <NewBadge />}
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubMenu className={styles.ratioMenu}>
                      {service.subItems.map((subItem) => (
                        <DropdownMenu.Item
                          key={subItem.id}
                          data-testid={`integrations-bar-${service.id}-${subItem.id}`}
                          disabled={disableBar}
                          onClick={(): void =>
                            onServiceSubItemClick(service, subItem)
                          }
                        >
                          <SubItemLabel subItem={subItem} />
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.SubMenu>
                  </DropdownMenu.Sub>
                ) : (
                  <DropdownMenu.Item
                    onClick={(): void => onServiceClick(service)}
                    key={service.id}
                    data-testid={`integrations-bar-${service.id}`}
                    disabled={disableBar}
                  >
                    <Flex align="center" gap="2xs">
                      {service.icon} {service.title}
                    </Flex>
                    {service.isNew && <NewBadge />}
                  </DropdownMenu.Item>
                ),
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        </Button.Group>

        {children && <div className={styles.childrenArea}>{children}</div>}
      </div>

      {/* GENERAL MODAL FOR INTEGRATIONS */}
      <SearchImagesModal
        open={!!activeService?.openSearchImagesModal}
        handleClose={(): void => activeService?.onClose?.()}
        service={activeService}
        context={context}
        container={container}
      />
    </>
  )
}
